// Taste archetypes — collectible identity badges derived purely from the
// distilled profile (no enrichment, so they render on public pages offline).
// Each viewer earns a ranked set; the strongest is their "type". These are the
// social, shareable read on a taste that Letterboxd never surfaces.

import type { DistilledProfile } from "./types.ts";
import { computeTasteSignals } from "./tasteSignals.ts";

export interface Archetype {
  key: string;
  title: string;
  blurb: string;
  emoji: string;
  /** 0..1 how strongly this fits — used to rank and to pick the primary. */
  strength: number;
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Ranked archetypes, strongest first. Always returns at least one. */
export function computeArchetypes(profile: DistilledProfile): Archetype[] {
  const s = computeTasteSignals(profile);
  const byDecade = profile.aggregates.byDecade;
  const decadeTotal = Object.values(byDecade).reduce((a, b) => a + b, 0) || 1;
  const preShare = (cut: number) =>
    Object.entries(byDecade).reduce((a, [d, c]) => a + (Number(d) < cut ? c : 0), 0) / decadeTotal;
  const postShare = (cut: number) =>
    Object.entries(byDecade).reduce((a, [d, c]) => a + (Number(d) >= cut ? c : 0), 0) / decadeTotal;

  const cands: Archetype[] = [];
  const add = (key: string, title: string, blurb: string, emoji: string, strength: number) => {
    if (strength > 0) cands.push({ key, title, blurb, emoji, strength: clamp01(strength) });
  };

  // Rating personality
  if (s.avgRating >= 3.8) add("enthusiast", "The Enthusiast", "Finds the good in almost everything.", "🤗", (s.avgRating - 3.8) / 1.2 + 0.35);
  if (s.avgRating > 0 && s.avgRating < 3.1) add("tough-room", "The Tough Room", "Stars are earned, never given.", "⚖️", (3.1 - s.avgRating) / 2 + 0.35);

  // Decade shape
  if (s.definingDecade && s.definingDecadeShare >= 0.32)
    add("loyalist", `The ${s.definingDecade} Loyalist`, `${Math.round(s.definingDecadeShare * 100)}% of all watches land in one decade.`, "📼", s.definingDecadeShare + 0.15);
  add("classicist", "The Classicist", "Lives before the blockbuster era.", "🎞️", preShare(1980) >= 0.35 ? preShare(1980) + 0.1 : 0);
  add("now-playing", "Now Playing", "Firmly a viewer of the present.", "🍿", postShare(2010) >= 0.6 ? postShare(2010) - 0.15 : 0);
  add("time-traveler", "The Time Traveler", "Watches across the whole century.", "🕰️", s.erasSpanned >= 9 ? (s.erasSpanned - 8) / 5 + 0.4 : 0);

  // Logging habits
  add("completionist", "The Completionist", "Rates almost every film watched.", "✅", s.ratedShare >= 0.85 ? s.ratedShare : 0);
  add("lurker", "The Lurker", "Watches far more than scores.", "👀", s.library > 50 && s.ratedShare < 0.35 ? 0.4 + (0.35 - s.ratedShare) : 0);
  add("marathoner", "The Marathoner", "A four-figure library and climbing.", "🏃", s.library >= 1000 ? 0.5 + Math.min(0.4, (s.library - 1000) / 4000) : 0);
  add("backlog-baron", "The Backlog Baron", "An ever-growing watchlist.", "🗄️", s.backlog >= 150 ? 0.35 + Math.min(0.4, (s.backlog - 150) / 600) : 0);

  cands.sort((a, b) => b.strength - a.strength);
  if (cands.length === 0)
    cands.push({ key: "cinephile", title: "The Cinephile", blurb: "A taste all its own.", emoji: "🎬", strength: 0.5 });
  return cands;
}
